import { CoreInterfaces, DTOs } from "./../../core/Models";
import * as Constants from "./../../core/Constants";
import { servicesConfiguration } from "./../../core/Configurations/Services.config";
import * as StateProcessors from "./../../core/Context/state-processors";
import { questionValueProcessors } from "./../processors/QuestionValueProcessors";
import { getServicesForBusinessUnit } from "./../calculation";
import { t } from "i18next";

function formQuestionDTO(
  questionConfiguration: CoreInterfaces.QuestionConfiguration
): DTOs.QuestionDTO {
  const object: DTOs.QuestionDTO = {
    data: {
      code: questionConfiguration.Code,
      text: `Questions.${questionConfiguration.Code}.Text`,
      translationParams: {},
      inputType: questionConfiguration.InputType,
      defaultValue: questionConfiguration.DefaultValue,
      integration: `Questions.${questionConfiguration.Code}.Integration`,
      comment: "",
      tooltipText: `Questions.${questionConfiguration.Code}.TooltipText`,
      options: [],
      price: 0, // to be added from config
      time: 0, // to be added from config
      userValue: questionConfiguration.DefaultValue,
      userPrice: 0,
      userTime: 0,
      inputConstraints: {},
      businessUnits: questionConfiguration.BusinessUnits,
    },
    state: {
      isShown: true,
      isFilled: false,
      isInfoTextVariat: questionConfiguration.IsInfoTextVariat,
      isVisibleInUI: questionConfiguration.IsVisible,
    },
  };

  if (
    (questionConfiguration.InputType === Constants.QuestionInputType.Select ||
      questionConfiguration.InputType ===
        Constants.QuestionInputType.Checkbox) &&
    !!questionConfiguration.Options
  ) {
    object.data.options = questionConfiguration.Options.map((option) => ({
      key: option.Code,
      value: `Options.${option.Code}`,
    }));
    if (questionConfiguration.DefaultValue) {
      const selectedOption = object.data.options.find(
        (eachOption) => eachOption.key === questionConfiguration.DefaultValue
      );
      if (!!selectedOption) {
        object.data.userValue = selectedOption.key;
      }
    }
  }
  if (
    questionConfiguration.InputType ===
      Constants.QuestionInputType.MultipleSelect &&
    Array.isArray(questionConfiguration.Options) &&
    questionConfiguration.Options.length > 0
  ) {
    object.data.options = questionConfiguration.Options.map((option) => ({
      key: option.Code,
      value: `Options.${option.Code}`,
    }));
    if (questionConfiguration.DefaultValue) {
      const selectedOption = object.data.options.find(
        (eachOption) => eachOption.key === questionConfiguration.DefaultValue
      );
      if (!!selectedOption) {
        object.data.userValue = selectedOption.key;
      }
    }
  }
  if (
    questionConfiguration.InputType ===
    Constants.QuestionInputType.RichTextEditor
  ) {
    object.data.defaultValue = questionConfiguration.DefaultValue
      ? t(`Questions.${questionConfiguration.DefaultValue}.DefaultValue`)
      : "";
    object.data.userValue = questionConfiguration.DefaultValue
      ? t(`Questions.${questionConfiguration.Code}.DefaultValue`)
      : "";
  }

  if (questionConfiguration.InputType === Constants.QuestionInputType.Text) {
    object.data.defaultValue = questionConfiguration.DefaultValue
      ? t(`Questions.${questionConfiguration.DefaultValue}.DefaultValue`)
      : "";
    object.data.userValue = questionConfiguration.DefaultValue
      ? t(`Questions.${questionConfiguration.Code}.DefaultValue`)
      : "";
  }

  if (
    questionConfiguration.InputType === Constants.QuestionInputType.Numeric ||
    questionConfiguration.InputType === Constants.QuestionInputType.Currency ||
    questionConfiguration.InputType === Constants.QuestionInputType.RangeNumber
  ) {
    object.data.inputConstraints.min =
      questionConfiguration.InputConstraints.min;
    object.data.inputConstraints.max =
      questionConfiguration.InputConstraints.max;
    object.data.inputConstraints.maxLength =
      questionConfiguration.InputConstraints.maxLength;
  }

  if (
    questionConfiguration.InputType ===
    Constants.QuestionInputType.MultipleSelect
  ) {
    object.data.inputConstraints.maxEntrances =
      questionConfiguration.InputConstraints.maxEntrances;
    object.data.inputConstraints.hasDefaultOptions =
      questionConfiguration.InputConstraints.hasDefaultOptions;
    object.data.inputConstraints.hasVisibleTooltip =
      questionConfiguration.InputConstraints.hasVisibleTooltip;
  }

  if (
    questionConfiguration.InputType === Constants.QuestionInputType.Text ||
    questionConfiguration.InputType === Constants.QuestionInputType.Multiline
  ) {
    object.data.inputConstraints.format =
      questionConfiguration.InputConstraints.format;
    object.data.inputConstraints.maxLength =
      questionConfiguration.InputConstraints.maxLength;
  }
  if (questionConfiguration.InputType === Constants.QuestionInputType.Date) {
    object.data.inputConstraints.format =
      questionConfiguration.InputConstraints.format;
    object.data.inputConstraints.allowFutureDate =
      questionConfiguration.InputConstraints.allowFutureDate;
  }
  /**
   * Instantiate the FullDate questions with an array of nulls to prevent the #17310 issue
   * Normally this should be fixed via default value of the question, but this is a string at the moment.
   * If we modify it's type we should consider the questions that was previously saved, and this might affect the previously saved engagements
   */
  if (
    questionConfiguration.InputType === Constants.QuestionInputType.FullDate
  ) {
    object.data.userValue = [null, null];
  }

  if (
    questionConfiguration.InputType ===
      Constants.QuestionInputType.MultipleSelect &&
    questionConfiguration.InputConstraints?.hasVisibleTooltip
  ) {
    if (
      !object.data.userValue ||
      object.data.userValue === Constants.HelpfulConstants.PleaseSelect
    ) {
      object.data.userValue = object.data.options.map((option) => option.key);
    }
  }
  return object;
}

export function formTaskDTO(
  taskConfiguration?: CoreInterfaces.TaskConfiguration
): DTOs.TaskDTO {
  const object: DTOs.TaskDTO = {
    data: {
      code: null,
      type: null,
      title: null,
      timeDue: null,
      tooltip: null,
      calculationTooltip: null,
      visibility: [],
      frequency: null,
      allocationOfResponsibility: null,
      canModifyAdjustmentFreq: true,
      canModifyAllocationResponsibility: true,
      canModifyStaffLevel: true,
      comment: null,
      calculationMethod: null,
      calculationQuestion: null,
      calculationQuestionOverride: null,
      estimatedTimePerUnit: 0,
      staffLevel: null,
      serviceTaskGroup: null,
      adjustments: {
        frequency: null,
        allocationOfResponsibility: null,
        comment: null,
        estimatedTimePerUnit: null,
        staffLevel: null,
      },
      sumTimeInHrsAndMinutes: 0,
      calculationTask: null,
    },
    state: {
      isVisible: true,
      isActive: false,
    },
  };

  if (!!taskConfiguration) {
    object.data.code = taskConfiguration.Code;
    object.data.type = taskConfiguration.Type;
    object.data.title = `Tasks.${taskConfiguration.Code}.Text`;
    object.data.timeDue = "";
    object.data.tooltip = `Tasks.${taskConfiguration.Code}.TooltipText`;
    object.data.calculationTooltip = `Tasks.${taskConfiguration.Code}.CalculationTooltipText`;
    object.data.visibility = taskConfiguration.Visibility;
    object.data.frequency = taskConfiguration.DefaultFreq;
    object.data.allocationOfResponsibility =
      taskConfiguration.DefaultAllocationOfResponsability;
    object.data.canModifyAdjustmentFreq =
      taskConfiguration.CanModifyAdjustmentFreq;
    object.data.canModifyAllocationResponsibility =
      taskConfiguration.CanModifyAllocationResponsibility;
    object.data.canModifyStaffLevel = taskConfiguration.CanModifyStaffLevel;
    object.data.comment = `Tasks.${taskConfiguration.Code}.Comment`;
    object.data.calculationMethod = taskConfiguration.CalculationMethod;
    object.data.calculationQuestion = taskConfiguration.CalculationQuestion;
    object.data.calculationTask = taskConfiguration.CalculationTask;
    object.data.estimatedTimePerUnit = taskConfiguration.EstimatedTimePerUnit;
    object.data.serviceTaskGroup = taskConfiguration.ServiceTaskGroup;
    object.data.staffLevel = taskConfiguration.StaffLevel;
    object.data.sumTimeInHrsAndMinutes = 0;
    object.state.isActive = taskConfiguration.IsActive;
  }

  return object;
}

function formServiceDTO(
  serviceConfiguration: CoreInterfaces.ServiceConfiguration
): DTOs.ServiceDTO {
  const object: DTOs.ServiceDTO = {
    data: {
      code: serviceConfiguration.Code,
      title: `Services.${serviceConfiguration.Code}.Title`,
      questions: serviceConfiguration.Questions.map((questionConfiguration) =>
        formQuestionDTO(questionConfiguration)
      ),
      tasks: serviceConfiguration.Tasks.map((taskConfiguration) =>
        formTaskDTO(taskConfiguration)
      ),
      calculationVariables: {
        amount: null,
        contactPerson: null,
        totalWorkCost: null,
      },
      staffingAdjustments: {
        cost: null,
        extendedPriceListPanelCost: null,
      },
      allocationSummaryPanel: {
        hasAveragePrice:
          serviceConfiguration.AllocationSummaryPanel.HasAveragePrice,
        hasAverageTime:
          serviceConfiguration.AllocationSummaryPanel.HasAverageTime,
        hasAdjustmentOfCost:
          serviceConfiguration.AllocationSummaryPanel.HasAdjustmentOfCost,
        hasTableHeader:
          serviceConfiguration.AllocationSummaryPanel.HasTableHeader,
        hasTotalCost: serviceConfiguration.AllocationSummaryPanel.HasTotalCost,
        isDividedByTaskServiceGroup:
          serviceConfiguration.AllocationSummaryPanel
            .IsDividedByTaskServiceGroup,
        shouldIncludeAdditionalCosts:
          serviceConfiguration.AllocationSummaryPanel
            .ShouldIncludeAdditionalCosts,
      },
      calculationPage: {
        hasCustomTitleOnServicePanel:
          serviceConfiguration.CalculationPage.HasCustomTitleOnStaffingPanel,
        calculationPanelTitle: `Services.${serviceConfiguration.Code}.Title`,
        staffingTableHeaderTitle: `General.StaffLevel`,
        totalCostText: `Services.${serviceConfiguration.Code}.Title`,
        sumText: "TableHeader.Sum",
      },
      otherServicesTotalsPanel: {
        isTotalCostOverridenByAdjustment:
          serviceConfiguration.OtherServicesTotalsPanel
            .IsTotalCostOverridenByAdjustment,
      },
      additionalCosts: [],
      recurringType: serviceConfiguration.ServiceRecurringType,
      serviceGroup: serviceConfiguration.ServiceGroup,
      serviceTaskGroups: serviceConfiguration.ServiceTaskGroups,
      priceList: null,
      internalNotes: "",
    },
    state: {
      isSelected: serviceConfiguration.IsSelectedByDefault,
      isVisibleInServicesPage: serviceConfiguration.ServicesPage.IsVisible,
      isVisibleInQuestionPage: serviceConfiguration.QuestionsPage.IsVisible,
      isVisibleInSideMenu: serviceConfiguration.SideMenu.IsVisible,
      isExpandedQuestionAccordion: true,
      isExpandedEngagementDescriptionAccordion: true,
      isExpandedPriceAndTimeCalculationAccordion: true,
      canBeSelectedInServicePage:
        serviceConfiguration.ServicesPage.IsSelectable,
    },
  };

  if (
    !!serviceConfiguration.AdditionalCosts &&
    serviceConfiguration.AdditionalCosts.length
  ) {
    object.data.additionalCosts = serviceConfiguration.AdditionalCosts.map(
      (additionalCostConfig) => {
        return formAdditionalCostDTO(additionalCostConfig);
      }
    );
  }

  if (serviceConfiguration.CalculationPage.HasCustomTitleOnStaffingPanel) {
    object.data.calculationPage.staffingTableHeaderTitle = `Services.${serviceConfiguration.Code}.StaffingTableHeader`;
  }

  if (serviceConfiguration.CalculationPage.HasCustomOtherTitlesOnServicePage) {
    object.data.calculationPage.calculationPanelTitle = `Services.${serviceConfiguration.Code}.TableHeader`;
    object.data.calculationPage.totalCostText = `Services.${serviceConfiguration.Code}.TotalSumText`;
  }

  if (serviceConfiguration.CalculationPage.HasCustomTextForTotalNumberOfHours) {
    object.data.calculationPage.sumText = `Services.${serviceConfiguration.Code}.TotalNumberOfHours`;
  }

  if (serviceConfiguration.CalculationVariables) {
    object.data.calculationVariables.amount =
      serviceConfiguration.CalculationVariables.Amount;
    object.data.calculationVariables.contactPerson =
      serviceConfiguration.CalculationVariables.ContactPersonQuestion;
    object.data.calculationVariables.totalWorkCost =
      serviceConfiguration.CalculationVariables.TotalWorkCost;
  }

  return object;
}

export function formServiceDTOs(
  servicesConfiguration: Array<CoreInterfaces.ServiceConfiguration>,
  serviceHandlingFn: Function = null
): Array<DTOs.ServiceDTO> {
  return (servicesConfiguration || []).map((eachServiceConfiguration) => {
    const service = formServiceDTO(eachServiceConfiguration);
    if (!!serviceHandlingFn) {
      serviceHandlingFn(service);
    }
    return service;
  });
}

function formAdditionalCostDTO(
  additionalCostConfig: CoreInterfaces.AdditionalCostConfiguration
): DTOs.AdditionalCostDTO {
  const object: DTOs.AdditionalCostDTO = {
    data: {
      code: additionalCostConfig.Code,
      text: `AdditionalCosts.${additionalCostConfig.Code}.Text`,
      cost: additionalCostConfig.Cost,
      fixedCost: additionalCostConfig.FixedCost ?? 0,
      minCost: 0,
      maxCost: 0,
      tooltip: `AdditionalCosts.${additionalCostConfig.Code}.TooltipText`,
      valueQuestionCode: additionalCostConfig.ValueQuestionCode,
      numberOfUnits: additionalCostConfig.NumberOfUnits,
      description: "",
      adjustments: {
        Cost: null,
        ExtendedPriceListPanelCost: null,
      },
      serviceTaskGroup: additionalCostConfig.ServiceTaskGroup,
      isFrontendObject: false,
    },
    state: {
      isVisible: true,
      isUsedInSoftwareServiceCostsTable:
        additionalCostConfig.UsedInSoftwareServiceCostsTable,
      isUsedInSoftwareServiceFixedPriceTable:
        additionalCostConfig.UsedInSoftwareServiceFixedPriceTable,
    },
  };
  if (
    !!additionalCostConfig.Adjustments &&
    !!additionalCostConfig.Adjustments.Cost
  ) {
    object.data.adjustments.Cost = additionalCostConfig.Adjustments.Cost;
  }
  return object;
}

export function formServiceDTOsFromServiceConfigurationWrite(
  engagementConfigurationPack: CoreInterfaces.EngagementConfigurationReadPack,
  applicationConfiguration: CoreInterfaces.ApplicationConfigurationReadPack,
  serviceHandlingFn: Function
): Array<DTOs.ServiceDTO> {
  let cleanServiceDTOs: Array<DTOs.ServiceDTO> = [];
  if (!!engagementConfigurationPack?.Services?.length) {
    const serviceWriteConfiguration = engagementConfigurationPack.Services;

    const params = new URLSearchParams(window.location.search);
    const businessUnit = params.get("unit");
    const serviceConfigurationFiltered = getServicesForBusinessUnit(
      servicesConfiguration,
      businessUnit as unknown as Constants.BusinessUnit
    );
    cleanServiceDTOs = formServiceDTOs(serviceConfigurationFiltered);
    if (!!applicationConfiguration) {
      cleanServiceDTOs =
        StateProcessors.updateServicesOnApplicationConfigurationChange(
          cleanServiceDTOs,
          applicationConfiguration
        );
    }

    if (cleanServiceDTOs.length) {
      for (const service of cleanServiceDTOs) {
        const serverServiceConfig = serviceWriteConfiguration.find(
          (eachServiceConfig) => eachServiceConfig.Code === service.data.code
        );
        if (!!serverServiceConfig) {
          //tasks
          for (const taskDTO of service.data.tasks) {
            const taskConfig = serverServiceConfig.Tasks.find(
              (taskConfig) => taskConfig.Code === taskDTO.data.code
            );
            if (!!taskConfig) {
              // handle task data
              taskDTO.data.title = taskConfig.Title;
              taskDTO.data.frequency = taskConfig.Frequency;
              taskDTO.data.estimatedTimePerUnit =
                taskConfig.EstimatedTimePerUnit;
              taskDTO.data.allocationOfResponsibility =
                taskConfig.AllocationOfResponsability;
              taskDTO.data.staffLevel = taskConfig.StaffLevel;
              taskDTO.data.comment = taskConfig.Comment;
              taskDTO.data.adjustments.frequency =
                taskConfig.Adjustments.Frequency;
              taskDTO.data.adjustments.allocationOfResponsibility =
                taskConfig.Adjustments.AllocationOfResponsability;
              taskDTO.data.adjustments.staffLevel =
                taskConfig.Adjustments.StaffLevel;
              taskDTO.data.adjustments.comment = taskConfig.Adjustments.Comment;
              taskDTO.data.adjustments.estimatedTimePerUnit =
                taskConfig.Adjustments.EstimatedTimePerUnit;
              taskDTO.data.timeDue = taskConfig.TimeDue;
              taskDTO.state.isActive = taskConfig.IsActive;
            }
          }

          const manuallyAddedTasks = serverServiceConfig.Tasks.filter(
            (taskConfig) => taskConfig.Type === Constants.TaskType.ManuallyAdded
          );
          for (const taskConfig of manuallyAddedTasks) {
            const taskDTO = formTaskDTO();
            taskDTO.data.code = taskConfig.Code;
            taskDTO.data.type = taskConfig.Type;
            taskDTO.data.title = taskConfig.Title;
            taskDTO.data.timeDue = taskConfig.TimeDue;
            taskDTO.data.visibility = taskConfig.Visibility;
            taskDTO.data.frequency = taskConfig.Frequency;
            taskDTO.data.allocationOfResponsibility =
              taskConfig.AllocationOfResponsability;
            taskDTO.data.canModifyAdjustmentFreq = true;
            taskDTO.data.canModifyAllocationResponsibility = true;
            taskDTO.data.canModifyStaffLevel = false;
            taskDTO.data.comment = taskConfig.Comment;
            taskDTO.data.calculationMethod =
              Constants.TaskCalculationMethod.TimeXPricePerHour;
            taskDTO.data.estimatedTimePerUnit = taskConfig.EstimatedTimePerUnit;
            taskDTO.data.staffLevel = taskConfig.StaffLevel;
            taskDTO.data.sumTimeInHrsAndMinutes =
              taskConfig.SumTimeInHrsAndMinutes;
            taskDTO.data.adjustments.frequency =
              taskConfig.Adjustments.Frequency;
            taskDTO.data.adjustments.allocationOfResponsibility =
              taskConfig.Adjustments.AllocationOfResponsability;
            taskDTO.data.adjustments.comment = taskConfig.Adjustments.Comment;
            taskDTO.data.adjustments.estimatedTimePerUnit =
              taskConfig.Adjustments.EstimatedTimePerUnit;
            taskDTO.data.adjustments.staffLevel =
              taskConfig.Adjustments.StaffLevel;
            taskDTO.data.serviceTaskGroup = taskConfig.ServiceTaskGroup;
            taskDTO.state.isActive = taskConfig.IsActive;

            if (
              serverServiceConfig.ServiceRecurringType ===
              Constants.ServiceRecurringType.Monthly
            ) {
              taskDTO.data.canModifyAdjustmentFreq = true;
              taskDTO.data.calculationMethod =
                Constants.TaskCalculationMethod.FrequencyXTimeXPricePerHour;
            }

            if (
              serverServiceConfig.Code === Constants.ServiceCode.ExtraServices
            ) {
              taskDTO.data.canModifyStaffLevel = true;
            }

            const index = serverServiceConfig.Tasks.findIndex(
              (taskConfig) => taskConfig.Code === taskDTO.data.code
            );
            service.data.tasks = [
              ...service.data.tasks.slice(0, index),
              taskDTO,
              ...service.data.tasks.slice(index),
            ];
          }

          //questions
          for (const questionDTO of service.data.questions) {
            const questionConfig = serverServiceConfig.Questions.find(
              (questionConfig) => questionConfig.Code === questionDTO.data.code
            );
            if (!!questionConfig) {
              questionDTO.data.defaultValue = questionConfig.DefaultValue;
              questionDTO.state.isFilled = questionConfig.UserInput.IsFilled;
              questionDTO.data.comment = questionConfig.UserInput.Comment;

              const questionDataProcessor =
                questionValueProcessors[questionDTO.data.code];
              if (!!questionDataProcessor) {
                questionDataProcessor(questionDTO, questionConfig);
              } else {
                questionDTO.data.userValue = questionConfig.UserInput.Answer;
                if (
                  questionDTO.data.inputType ===
                    Constants.QuestionInputType.Date &&
                  questionConfig.UserInput.Answer !== null &&
                  questionConfig.UserInput.Answer !== ""
                ) {
                  questionDTO.data.userValue = new Date(
                    questionConfig.UserInput.Answer
                  );
                }
                if (
                  questionDTO.data.inputType ===
                  Constants.QuestionInputType.FullDate
                ) {
                  const dateStrings = JSON.parse(
                    questionConfig.UserInput.Answer
                  );
                  if (Array.isArray(dateStrings) && dateStrings.length > 0) {
                    const dateObjects = dateStrings.map((dateString: any) => {
                      if (dateString !== null) {
                        return new Date(dateString);
                      } else {
                        return null;
                      }
                    });
                    questionDTO.data.userValue = dateObjects;
                  }
                }

                if (
                  questionDTO.data.inputType ===
                    Constants.QuestionInputType.License ||
                  questionDTO.data.inputType ===
                    Constants.QuestionInputType.ContactPersonUserDetails ||
                  questionDTO.data.inputType ===
                    Constants.QuestionInputType.MultipleSelect ||
                  questionDTO.data.inputType ===
                    Constants.QuestionInputType.ClientDetails ||
                  questionDTO.data.inputType ===
                    Constants.QuestionInputType.SigneeDetails
                ) {
                  try {
                    questionDTO.data.userValue = JSON.parse(
                      questionConfig.UserInput.Answer
                    );
                  } catch (error) {
                    console.log(
                      "We found an error while parsing the value: ",
                      error
                    );
                    questionDTO.data.userValue = [] as unknown as string;
                  }

                  if (
                    questionDTO.data.inputType ===
                      Constants.QuestionInputType.MultipleSelect &&
                    questionDTO.data.inputConstraints.hasVisibleTooltip
                  ) {
                    if (
                      !questionDTO.data.userValue ||
                      questionDTO.data.userValue ===
                        Constants.HelpfulConstants.PleaseSelect
                    ) {
                      questionDTO.data.userValue = questionDTO.data.options.map(
                        (option) => option.key
                      );
                    }
                  }
                }
              }
            }
          }
          //additional costs
          if (!!service.data.additionalCosts) {
            for (const additionalServiceCostDTO of service.data
              .additionalCosts) {
              const additionalCostConfig =
                serverServiceConfig.AdditionalCosts.find(
                  (eachAdditionalCostConfig) =>
                    eachAdditionalCostConfig.Code ===
                    additionalServiceCostDTO.data.code
                );
              if (!!additionalCostConfig) {
                additionalServiceCostDTO.data.cost = additionalCostConfig.Cost;
                if (
                  additionalCostConfig.Adjustments &&
                  additionalCostConfig.Adjustments.Cost
                ) {
                  additionalServiceCostDTO.data.adjustments.Cost =
                    additionalCostConfig.Adjustments.Cost;
                }
              }
            }
          }

          //service
          service.data.staffingAdjustments.cost =
            serverServiceConfig.StaffingAdjustments.Cost;
          service.data.staffingAdjustments.extendedPriceListPanelCost =
            serverServiceConfig.StaffingAdjustments.ExtendedPriceListPanelCost;
          service.state.isSelected = serverServiceConfig.IsSelected;
          service.state.isExpandedQuestionAccordion =
            serverServiceConfig.IsExpandedQuestionAccordion;
          service.state.isExpandedEngagementDescriptionAccordion =
            serverServiceConfig.IsExpandedEngagementDescriptionAccordion;
          service.state.isExpandedPriceAndTimeCalculationAccordion =
            serverServiceConfig.IsExpandedPriceAndTimeCalculationAccordion;
          service.data.internalNotes = serverServiceConfig.InternalNotes;
          serviceHandlingFn(service);
        }
      }
    }
  }

  return cleanServiceDTOs;
}
